import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaymentDashboardData } from "../../../../redux/reducers/paymentDashboardSlice";
import "./BillingChart.css";


const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 'none',
    fontSize: theme.typography.pxToRem(12),
    border: 'none',
    padding: 0,
    boxShadow: 'none',
  },
}));

const OutstandingAndAging = () => {
  const dispatch = useDispatch();
  const { loading, outstandingAging } =
    useSelector((state) => state.paymentDashboard);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceCounts, setServiceCounts] = useState([]);


  useEffect(() => {
    dispatch(fetchPaymentDashboardData());
  }, [dispatch]);

  useEffect(() => {
    const categories = (outstandingAging?.agingBuckets ?? [])?.map((li) => li?.range)
    const counts = (outstandingAging?.agingBuckets ?? [])?.map((li) => Number(li?.totalValue ?? 0)?.toFixed(0))
    setServiceCategories(categories)
    setServiceCounts(counts)
  }, [outstandingAging])

  const revenueByCategory = {
    options: {
      chart: { type: "bar", toolbar: { show: false } },
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 4,
          columnWidth: "30%",
        },
      },
      colors: ["#004AAD", "#A7EDE7", "#000000", "#90C2FF", "#D3D3F3"],
      xaxis: {
        categories: serviceCategories,
        labels: {
          style: { fontSize: "14px", colors: "#000" },
        },
      },
      yaxis: {
        labels: { style: { fontSize: "12px" } },
      },
      dataLabels: { enabled: false },
      grid: { yaxis: { lines: { show: false } } },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const category = serviceCategories[dataPointIndex] || '';
          const reqData = outstandingAging?.agingBuckets?.[dataPointIndex] ?? {}

          return (
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
            category +
            '<span class="apexcharts-tooltip-marker" style="background-color: ' +
            w.globals.colors[dataPointIndex] +
            '; margin-left: 5px;"></span>' +
            "</div>" +
            '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
            '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Invoices: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(reqData?.invoiceCount ?? 0) +
            "</span>" +
            "</div>" +
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Total: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            Number(reqData?.totalValue ?? 0)?.toFixed(2) +
            "</span>" +
            "</div>" +
            "</div>" +
            "</div>"
          );
        },
      },
    },
    series: [
      {
        name: "Appointments",
        data: serviceCounts,
      },
    ],
  };

  const cards = [
    { title: 'Average DSO For Month', value: Number(outstandingAging?.avgDSO?.month?.value ?? 0)?.toFixed(2) },
    { title: 'Average DSO For Year', value: Number(outstandingAging?.avgDSO?.year?.value ?? 0)?.toFixed(2) },
  ];

  const getTooltipContent = (cardTitle, durationType) => {
    const reqData = outstandingAging?.avgDSO?.[durationType]
    return (
      <div>
        <div
          className="apexcharts-tooltip-title"
          style={{
            fontFamily: 'Helvetica, Arial, sans-serif',
            fontSize: '12px',
            backgroundColor: '#EBEBEB',
            color: '#000',
            padding: '8px',
            margin: 0,
          }}
        >
          {cardTitle}
        </div>
        <div
          className="apexcharts-tooltip-series-group"
          style={{
            order: 1,
            display: 'flex',
            backgroundColor: '#fff',
            padding: '8px',
          }}
        >
          <div
            className="apexcharts-tooltip-text"
            style={{
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: '12px',
              color: '#000',
            }}
          >
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Value:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {Number(reqData?.value ?? 0)?.toFixed(2)}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Outstanding:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {Number(reqData?.outstanding ?? 0)?.toFixed(2)}
              </span>
            </div>
            <div className="apexcharts-tooltip-y-group">
              <span className="apexcharts-tooltip-text-y-label">
                Sales:{' '}
              </span>
              <span className="apexcharts-tooltip-text-y-value">
                {Number(reqData?.sales ?? 0)?.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-wrapper">
      {/* Chart Section */}
      <div className="dashboard-container">
        {/* Chart 1 */}
        <div className="chart-section">
          <h4>Outstanding & Aging</h4>
          <Chart
            options={revenueByCategory?.options}
            series={revenueByCategory?.series}
            type="bar"
            height={300}
          />
        </div>
        <div className="dashboard-container">
          <div style={{ height: 350, alignItems: 'center', justifyContent: 'center', flex: 1, display: 'flex' }}>
            <div style={{ display: 'flex', gap: '20px' }}>
              {cards?.map((card, i) => {
                const tooltipContent = getTooltipContent(card?.title, i === 0 ? 'month' : 'year');
                return (
                  <HtmlTooltip
                    key={i}
                    title={tooltipContent}
                    arrow
                    followCursor
                    placement="top"
                  >
                    <div
                      style={{
                        padding: '1px 20px 20px 20px',
                        background: '#e9f2ff',
                        borderRadius: '16px',
                        flex: 1,
                      }}
                    >
                      <h4>{card.title}</h4>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ margin: 0 }}>{card.value}</h2>
                      </div>
                    </div>
                  </HtmlTooltip>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OutstandingAndAging;
